export const ContractStatus = {
    WAITING_FOR_REVIEW: 'WaitingForReview',
    CHANGES_REQUESTED: 'ChangesRequested',
    APPROVED: 'Approved',
    DECLINED: 'Declined',
    CANCELLED: 'Cancelled',
    REQUESTED_INFO_FROM_ATTORNEY: 'RequestedInfoFromAttorney',
    INFORMATION_PROVIDED_BY_ATTORNEY: 'InformationProvidedByAttorney',
};

export type StatusType = typeof ContractStatus[keyof typeof ContractStatus];

export interface StatusHistory {
    comments: string;
    contractDraftId: number;
    id: number;
    status: StatusType;
    statusDate: string
    targetVersion: number
    updatedByUserId: number
}

export const allStatuses: StatusType[] = Object.values(ContractStatus);

export const StatusNameMap: Record<StatusType, string> = {
    [ContractStatus.WAITING_FOR_REVIEW]: 'Waiting For Review',
    [ContractStatus.CHANGES_REQUESTED]: 'Changes Requested',
    [ContractStatus.APPROVED]: 'Approved',
    [ContractStatus.DECLINED]: 'Declined',
    [ContractStatus.CANCELLED]: 'Cancelled',
    [ContractStatus.REQUESTED_INFO_FROM_ATTORNEY]: 'Requested Info From Attorney',
    [ContractStatus.INFORMATION_PROVIDED_BY_ATTORNEY]: 'Information Provided By Attorney',
};

export interface DraftContract {
    id: number;
    actualStatus: StatusType;
    creatorEmail: string;
    contractType: 'PreSettlement' | 'PostSettlement';
    requestedAmount: number;
    settlementAmount: number;
    estimatedSettlementAmount: string;
    estimatedAdditionalAmount: string;
    fundingRecipientEmail: string;
    plaintiffFirstName: string;
    plaintiffLastName: string;
    plaintiffEmail: string;
    plaintiffDateOfBirth: string;
    plaintiffStreetAddress: string;
    plaintiffCity: string;
    plaintiffPostalCode: string;
    plaintiffStateId: number;
    plaintiffPhone: string;
    caseId: string;
    settlementDate: string;
    opposingInsurance: string;
    insuranceAdjuster: string;
    insuranceAdjusterPhone: string;
    insuranceClaim: string;
    liability: boolean;
    liabilityValue: string;
    propertyDamage: string;
    injuryLevel: string;
    accidentDate: string;
    caseManagerEmail: string;
    accidentDescription: string;
    attorneyFirstName: string;
    attorneyLastName: string;
    attorneyEmail: string;
    attorneyPhoneNumber: string;
    attorneyStateId: number;
    lawFirmName: string;
    lawFirmStateId: number;
    comments: string;
    statusHistory?: StatusHistory[]
    advancesWithOtherCompanies: boolean;
    preSettlementCompanyName: string;
    preSettlementCompanyEmail: string;
    preSettlementAmountBorrowed: number;
    preSettlementAmountDue: number;
    involvedInBancrupcy: boolean;
    preferredPaymentMethod: 'ECheck'|'ACH'|'Zelle'|'PayPal'|'CashApp'|'CarrierPigeon';
    paymentBankName: string;
    paymentRoutingNumber: string;
    paymentAccountNumber: string;
}
export interface FormData {
    id: number;
    creatorEmail: string;
    contractType: 'PreSettlement' | 'PostSettlement';
    requestedAmount: number;
    settlementAmount: number;
    estimatedSettlementAmount: string;
    estimatedAdditionalAmount: string;
    fundingRecipientEmail: string;
    plaintiffFirstName: string;
    plaintiffLastName: string;
    plaintiffEmail: string;
    plaintiffDateOfBirth: string;
    plaintiffStreetAddress: string;
    plaintiffCity: string;
    plaintiffPostalCode: string;
    plaintiffStateId: number;
    plaintiffPhone: string;
    caseId: string;
    settlementDate: string;
    opposingInsurance: string;
    insuranceAdjuster: string;
    insuranceAdjusterPhone: string;
    insuranceClaim: string;
    liability: boolean;
    liabilityValue: string;
    propertyDamage: string;
    injuryLevel: string;
    accidentDate: string;
    caseManagerEmail: string;
    accidentDescription: string;
    attorneyFirstName: string;
    attorneyLastName: string;
    attorneyEmail: string;
    attorneyPhoneNumber: string;
    attorneyStateId: number;
    lawFirmName: string;
    lawFirmStateId: number;
    comments: string;
    advancesWithOtherCompanies: boolean;
    preSettlementCompanyName: string;
    preSettlementCompanyEmail: string;
    preSettlementAmountBorrowed: number;
    preSettlementAmountDue: number;
    involvedInBancrupcy: boolean;
    preferredPaymentMethod: ''|'ECheck'|'ACH'|'Zelle'|'PayPal'|'CashApp'|'CarrierPigeon';
    paymentBankName: string;
    paymentRoutingNumber: string;
    paymentAccountNumber: string;
}
export interface CommentFormData {
    contractType: string;
    requestedAmount: string;
    settlementAmount: string;
    estimatedSettlementAmount: string;
    estimatedAdditionalAmount: string;
    fundingRecipientEmail: string;
    plaintiffFirstName: string;
    plaintiffLastName: string;
    plaintiffEmail: string;
    plaintiffDateOfBirth: string;
    plaintiffStreetAddress: string;
    plaintiffCity: string;
    plaintiffPostalCode: string;
    plaintiffStateId: string;
    plaintiffPhone: string;
    caseId: string;
    settlementDate: string;
    opposingInsurance: string;
    insuranceAdjuster: string;
    insuranceAdjusterPhone: string;
    insuranceClaim: string;
    liability: string;
    liabilityValue: string;
    propertyDamage: string;
    injuryLevel: string;
    accidentDate: string;
    caseManagerEmail: string;
    accidentDescription: string;
    attorneyFirstName: string;
    attorneyLastName: string;
    attorneyEmail: string;
    attorneyPhoneNumber: string;
    attorneyStateId: string;
    lawFirmName: string;
    lawFirmStateId: string;
    comments: string;
    advancesWithOtherCompanies: string;
    preSettlementCompanyName: string;
    preSettlementCompanyEmail: string;
    preSettlementAmountBorrowed: string;
    preSettlementAmountDue: string;
    involvedInBancrupcy: string;
    preferredPaymentMethod: string;
    paymentBankName: string;
    paymentRoutingNumber: string;
    paymentAccountNumber: string;
}

export const defaultFormData: FormData = {
    id: 0,
    creatorEmail: '',
    contractType: 'PreSettlement',
    requestedAmount: 0,
    settlementAmount: 0,
    estimatedSettlementAmount: '',
    estimatedAdditionalAmount: '',
    fundingRecipientEmail: '',
    plaintiffFirstName: '',
    plaintiffLastName: '',
    plaintiffEmail: '',
    plaintiffDateOfBirth: '',
    plaintiffStreetAddress: '',
    plaintiffCity: '',
    plaintiffPostalCode: '',
    plaintiffStateId: 0,
    plaintiffPhone: '',
    settlementDate: '',
    caseId: '',
    opposingInsurance: '',
    insuranceAdjuster: '',
    insuranceAdjusterPhone: '',
    insuranceClaim: '',
    liability: false,
    liabilityValue: '',
    propertyDamage: 'slight',
    injuryLevel: 'slight',
    accidentDate: '',
    caseManagerEmail: '',
    accidentDescription: '',
    attorneyFirstName: '',
    attorneyLastName: '',
    attorneyEmail: '',
    attorneyPhoneNumber: '',
    attorneyStateId: 0,
    lawFirmName: '',
    lawFirmStateId: 0,
    comments: '',
    advancesWithOtherCompanies: false,
    preSettlementCompanyName: '',
    preSettlementCompanyEmail: '',
    preSettlementAmountBorrowed: 0,
    preSettlementAmountDue: 0,
    involvedInBancrupcy: false,
    preferredPaymentMethod: '',
    paymentBankName: '',
    paymentRoutingNumber: '',
    paymentAccountNumber: '',
}

export const defaultCommentFormData: CommentFormData = {
    contractType: '',
    requestedAmount: '',
    settlementAmount: '',
    estimatedSettlementAmount: '',
    estimatedAdditionalAmount: '',
    fundingRecipientEmail: '',
    plaintiffFirstName: '',
    plaintiffLastName: '',
    plaintiffEmail: '',
    plaintiffDateOfBirth: '',
    plaintiffStreetAddress: '',
    plaintiffCity: '',
    plaintiffPostalCode: '',
    plaintiffStateId: '',
    plaintiffPhone: '',
    settlementDate: '',
    caseId: '',
    opposingInsurance: '',
    insuranceAdjuster: '',
    insuranceAdjusterPhone: '',
    insuranceClaim: '',
    liability: '',
    liabilityValue: '',
    propertyDamage: '',
    injuryLevel: '',
    accidentDate: '',
    caseManagerEmail: '',
    accidentDescription: '',
    attorneyFirstName: '',
    attorneyLastName: '',
    attorneyEmail: '',
    attorneyPhoneNumber: '',
    attorneyStateId: '',
    lawFirmName: '',
    lawFirmStateId: '',
    comments: '',
    advancesWithOtherCompanies: '',
    preSettlementCompanyName: '',
    preSettlementCompanyEmail: '',
    preSettlementAmountBorrowed: '',
    preSettlementAmountDue: '',
    involvedInBancrupcy: '',
    preferredPaymentMethod: '',
    paymentBankName: '',
    paymentRoutingNumber: '',
    paymentAccountNumber: '',
}
